import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { initializeGooglePayPaymentRequestInWeb } from '../../actions/googlePay';
import { initializePaypalIfExpressCheckout } from '../../actions/paypal';
import { getTotalCost } from '../../containers/ticketSelection/utils/getTotalCost';
import {
    getAreEventbriteLegacyPaymentMethodsDisabled,
    getIsAppRebrandActive,
    getLocaleProps,
} from '../../selectors/app';
import {
    getApplePayExpressPaymentMethodStatus,
    getApplePayInstance,
    hasApplePayAsyncErrors,
    isExpressApplePayEnabled,
} from '../../selectors/applePay';
import {
    getGooglePayExpressPaymentMethodStatus,
    getGooglePayScriptStatus,
    isExpressGooglePayEnabled,
} from '../../selectors/googlePay';
import { isSeriesInfiniteCalendar } from '../../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import {
    getPayPalExpressPaymentMethodStatus,
    getPaypalScriptStatus,
    isExpressPaypalEnabled,
} from '../../selectors/paypal';
import {
    isOnlyCheckoutPage,
    isTicketsCompactPage,
} from '../../selectors/routing';
import { getSelectedTickets } from '../../selectors/tickets/getSelectedTickets';
import { hasSelectedTickets } from '../../selectors/tickets/hasSelectedTickets';
import { CheckoutState } from '../../state';
import { GooglePayScriptStatus } from '../../state/googlePay';
import { PaypalScriptStatus } from '../../state/paypal';
import { ExpressCheckoutProps } from './ExpressCheckout';

const _mapStateToProps = (
    state: CheckoutState,
): Pick<
    ExpressCheckoutProps,
    | 'areAllSelectedTicketsFree'
    | 'hasApplePayInstance'
    | 'hasAsyncApplePayErrors'
    | 'isSeriesInfiniteCalendar'
    | 'areTicketsSelected'
    | 'isAppRebrandActive'
    | 'isCompactSize'
    | 'isExpressApplePayEnabled'
    | 'isExpressGooglePayEnabled'
    | 'isExpressPaymentButtonDisabled'
    | 'isExpressPayPalEnabled'
    | 'isGooglePayScriptInitialized'
    | 'isOnlyCheckoutPage'
    | 'isPaypalScriptInitialized'
    | 'locale'
    | 'paypalExpressPaymentMethodStatus'
    | 'googlePayExpressPaymentMethodStatus'
    | 'applePayExpressPaymentMethodStatus'
    | 'areEventbriteLegacyPaymentMethodsDisabled'
> => {
    const areTicketsSelected = hasSelectedTickets(state);
    const isExpressPaymentButtonDisabled =
        !areTicketsSelected !== undefined ? !areTicketsSelected : true;
    const selectedTickets = getSelectedTickets(state);

    return {
        areAllSelectedTicketsFree: getTotalCost(selectedTickets) === 0,
        areTicketsSelected,
        hasApplePayInstance: !!getApplePayInstance(state),
        hasAsyncApplePayErrors: hasApplePayAsyncErrors(state),
        isSeriesInfiniteCalendar: isSeriesInfiniteCalendar(state),
        isAppRebrandActive: getIsAppRebrandActive(state),
        isCompactSize: isTicketsCompactPage(state),
        isExpressApplePayEnabled: isExpressApplePayEnabled(state),
        isExpressGooglePayEnabled: isExpressGooglePayEnabled(state),
        isExpressPaymentButtonDisabled,
        isExpressPayPalEnabled: isExpressPaypalEnabled(state),
        isGooglePayScriptInitialized:
            getGooglePayScriptStatus(state) === GooglePayScriptStatus.loaded,
        isOnlyCheckoutPage: isOnlyCheckoutPage(state),
        isPaypalScriptInitialized:
            getPaypalScriptStatus(state) === PaypalScriptStatus.loaded,
        locale: getLocaleProps(state).defaultLocale,
        paypalExpressPaymentMethodStatus:
            getPayPalExpressPaymentMethodStatus(state),
        googlePayExpressPaymentMethodStatus:
            getGooglePayExpressPaymentMethodStatus(state),
        applePayExpressPaymentMethodStatus:
            getApplePayExpressPaymentMethodStatus(state),
        areEventbriteLegacyPaymentMethodsDisabled:
            getAreEventbriteLegacyPaymentMethodsDisabled(state),
    };
};

const _mapDispatchToProps = (
    dispatch: Dispatch<CheckoutState>,
): Pick<
    ExpressCheckoutProps,
    'initializePaypal' | 'initializeGooglePayPaymentRequest'
> => ({
    initializePaypal: () => {
        dispatch(initializePaypalIfExpressCheckout());
    },
    initializeGooglePayPaymentRequest: () =>
        dispatch(initializeGooglePayPaymentRequestInWeb()),
});

export type ConnectedProps = ReturnType<typeof _mapStateToProps> &
    ReturnType<typeof _mapDispatchToProps>;

export const connectExpressCheckout = connect(
    _mapStateToProps,
    _mapDispatchToProps,
);
